<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { Filters } from "~/core/models/filters";
import { useGetCampaign } from "~/modules/campaign/composables/useGetCampaign";
import { PATH_CAMPAIGNS } from "~/modules/campaign/constants";
import { CampaignOMStatus } from "~/modules/campaign/models/CampaignOMStatus.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import ILxcPlus from "~icons/lxc/plus";

const { t } = useI18n();
const route = useRoute();
const { currentOrganization } = storeToRefs(useOrganizationStore());
const { selectedSectorCodes } = storeToRefs(useSectorStore());
const uid = route.params.uid.toString();
const {
  fetchData: fetchCampaign,
  isLoading,
  results: campaign,
  error,
  setFilter,
} = useGetCampaign({
  organizationCode: currentOrganization.value?.code,
  uid: uid,
});

const canUpdateOrCancelCampaign = computed(
  () =>
    campaign.value?.status === CampaignOMStatus.SCHEDULED ||
    campaign.value?.status === CampaignOMStatus.RUNNING,
);

const showCampaignOperationMgrFunnel: Ref<boolean> = ref(false);

async function loadData() {
  setFilter(Filters.SECTORS, selectedSectorCodes.value || []);
  await fetchCampaign();
}

function saved() {
  loadData();
  showCampaignOperationMgrFunnel.value = false;
}

onMounted(loadData);

watch(selectedSectorCodes, () => {
  loadData();
});
</script>

<template>
  <page-component
    :is-loading="isLoading"
    :error="error"
    :name="campaign?.name ?? ''"
    :should-not-translate="true"
    :back-default-route="PATH_CAMPAIGNS"
    check-license
  >
    <template #top-right>
      <lxc-button
        v-if="canUpdateOrCancelCampaign"
        :icon="ILxcPlus"
        size="large"
        type="primary"
        :title="t('campaign.details.buttons.edit.label')"
        :disabled="isLoading"
        @click="showCampaignOperationMgrFunnel = true"
      >
        {{ t("campaign.details.buttons.edit.label") }}
      </lxc-button>
    </template>
    <div class="flex gap-[1.875rem]">
      <campaign-operation-mgr-characteristics
        :campaign="campaign"
        class="flex-1"
        @reload-campaign="loadData"
      />
      <campaign-operation-mgr-statistics :campaign="campaign" class="flex-1" />
    </div>
    <div class="mt-8">
      <campaign-operation-mgr-operation-list :campaign="campaign" />
    </div>

    <campaign-operation-mgr-funnel
      v-model:show="showCampaignOperationMgrFunnel"
      :campaign="campaign"
      @saved="saved"
    />
  </page-component>
</template>
